import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Modal, QRCode, Alert, Upload } from 'antd';
import { UserOutlined, LockOutlined, HomeOutlined, MobileOutlined, CameraOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { subscriptionAPI } from '../../utils/api';

const { Title, Text } = Typography;

const features = [
  { icon: '🧬', text: 'Biometric fingerprint check-in' },
  { icon: '📊', text: 'Real-time attendance tracking' },
  { icon: '💳', text: 'Payment & membership management' },
  { icon: '🎂', text: 'Birthday & anniversary alerts' },
];

const buildUpiLink = (payment) => {
  const params = new URLSearchParams({
    pa: payment.upi_id,
    pn: payment.upi_name || 'Wellness',
    am: String(payment.amount),
    cu: 'INR',
    tn: `Wellness subscription - ${payment.center_name || ''}`,
  });
  return `upi://pay?${params.toString()}`;
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [expiredPayment, setExpiredPayment] = useState(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [paymentForm] = Form.useForm();
  const { login } = useAuth();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result?.subscriptionRequired) {
        const paymentData = result.data.payment;
        const pendingPaymentData = result.data.pendingPayment;
        
        // Include pending payment info
        setExpiredPayment({
          ...paymentData,
          pendingPayment: pendingPaymentData
        });
        
        // Set pricing info from payment data (already includes dynamic calculation)
        if (paymentData.memberCount !== undefined) {
          setPricingInfo({
            memberCount: paymentData.memberCount,
            amount: paymentData.amount,
            calculation: paymentData.calculation,
            pricingTiers: {
              tier1: { description: '≤100 members', fee: '₹1000 flat' },
              tier2: { description: '>100 members', fee: '₹5 per member' }
            },
            currentTier: paymentData.memberCount <= 100 ? 'tier1' : 'tier2'
          });
        }
        
        paymentForm.resetFields();
        
        if (pendingPaymentData) {
          if (pendingPaymentData.in_grace_period) {
            message.info('Your payment is under review. You have access during the grace period.');
          } else {
            message.warning('Your payment is pending approval. Access will be restored after approval.');
          }
        } else {
          message.warning('Your trial or subscription has expired. Please submit payment for verification.');
        }
      }
    } catch {
      message.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (values) => {
    if (!expiredPayment) return;

    try {
      setSubmittingPayment(true);
      await subscriptionAPI.submitPaymentProof({
        admin_id: expiredPayment.admin_id,
        admin_email: expiredPayment.admin_email,
        upi_transaction_id: values.upi_transaction_id,
        payment_proof_photo: paymentProofFile,
      });
      message.success('Payment submitted for verification. You will be notified after approval.');
      setExpiredPayment(null);
      paymentForm.resetFields();
      setPaymentProofFile(null);
      setPaymentProofPreview(null);
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to submit payment proof');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleFileChange = (file) => {
    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    // Validate file size (5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }

    setPaymentProofFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPaymentProofPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  const handleRemoveFile = () => {
    setPaymentProofFile(null);
    setPaymentProofPreview(null);
  };

  return (
    <>
      {/* ── Global reset so nothing causes overflow ── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body, html, #root { height: 100%; overflow: hidden; }

        .login-root {
          display: flex;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          position: relative;
        }

        /* ─── BACK TO HOME BUTTON ─── */
        .back-to-home {
          position: absolute;
          top: 24px;
          right: 24px;
          z-index: 10;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 10px;
          color: #fff;
          font-size: 13.5px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .back-to-home:hover {
          background: rgba(255, 255, 255, 0.20);
          border-color: rgba(255, 255, 255, 0.30);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
          color: #fff;
        }
        .back-to-home .anticon {
          font-size: 16px;
        }

        /* ─── LEFT PANEL ─── */
        .login-left {
          flex: 1;
          min-height: 100vh;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 24px;
          overflow-y: auto;
        }

        /* ─── RIGHT PANEL ─── */
        .login-right {
          flex: 0 0 48%;
          width: 48%;
          min-height: 100vh;
          background: #001529;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .blob { position: absolute; border-radius: 50%; pointer-events: none; }
        .blob-1 { width: 340px; height: 340px; background: rgba(24,144,255,0.08); top: -90px; right: -90px; }
        .blob-2 { width: 240px; height: 240px; background: rgba(24,144,255,0.06); bottom: -50px; left: -70px; }
        .blob-3 { width: 140px; height: 140px; background: rgba(24,144,255,0.07); bottom: 38%; right: 24px; }

        .right-content {
          position: relative;
          z-index: 2;
          width: 76%;
          max-width: 340px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0;
        }
        .logo-chip {
          width: 58px; height: 58px;
          border-radius: 15px;
          background: rgba(24,144,255,0.15);
          border: 1.5px solid rgba(24,144,255,0.35);
          display: flex; align-items: center; justify-content: center;
          font-size: 26px;
          margin-bottom: 26px;
          backdrop-filter: blur(6px);
          flex-shrink: 0;
        }
        .brand-title {
          color: #fff !important;
          font-size: 42px !important;
          font-weight: 800 !important;
          line-height: 1.13 !important;
          letter-spacing: -0.8px !important;
          margin-bottom: 14px !important;
          white-space: nowrap !important;
          text-align: center !important;
        }
        .brand-tagline {
          color: rgba(255,255,255,0.72);
          font-size: 13.5px;
          line-height: 1.72;
          display: block;
          margin-bottom: 28px;
          text-align: center;
        }
        .feature-list {
          display: flex;
          flex-direction: column;
          gap: 9px;
          width: 100%;
          margin-bottom: 36px;
        }
        .feature-item {
          display: flex;
          align-items: center;
          gap: 11px;
          background: rgba(24,144,255,0.10);
          border: 1px solid rgba(24,144,255,0.22);
          border-radius: 11px;
          padding: 11px 16px;
          white-space: nowrap;
        }
        .feature-item span.fi { font-size: 16px; flex-shrink: 0; line-height: 1; }
        .feature-item span.ft {
          color: rgba(255,255,255,0.90);
          font-size: 13px;
          font-weight: 500;
        }
        .version-tag { color: rgba(255,255,255,0.38); font-size: 11px; }

        /* ─── CARD PANEL ─── */
        .login-card-panel {
          flex: 1;
          min-height: 100vh;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 24px;
          overflow-y: auto;
        }
        .login-card {
          width: 100%;
          max-width: 400px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.10);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .card-accent {
          height: 5px;
          background: linear-gradient(90deg, #001529, #1890ff, #096dd9);
        }
        .card-body { padding: 34px 36px 28px; }

        .card-head { text-align: center; margin-bottom: 22px; }
        .head-avatar { font-size: 34px; display: block; margin-bottom: 8px; }
        .card-title {
          color: #1e293b !important;
          font-size: 21px !important;
          font-weight: 700 !important;
          margin: 0 0 5px 0 !important;
        }
        .card-sub { color: #94a3b8; font-size: 13px; display: block; }

        .form-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
          margin: 0 0 22px 0;
        }

        .field-label { color: #475569; font-weight: 600; font-size: 12.5px; letter-spacing: 0.2px; }

        /* REMOVED INNER SHADOW FROM INPUTS */
        .login-input .ant-input,
        .login-input .ant-input-affix-wrapper,
        .login-input input {
          border-radius: 10px !important;
          // border: 1.5px solid #e2e8f0 !important;
          // background: #f8fafc !important;
          font-size: 11.5px !important;
          // height: 44px !important;
          transition: border-color .2s !important;
          box-shadow: none !important; /* Remove inner shadow */
        }
        .login-input .ant-input-affix-wrapper {
          height: 44px !important;
          padding: 0 11px !important;
          align-items: center !important;
        }
        .login-input .ant-input-affix-wrapper:hover,
        .login-input .ant-input-affix-wrapper:focus-within {
          border-color: #1890ff !important;
          box-shadow: 0 0 0 3px rgba(24,144,255,0.12) !important; /* Only outer glow */
        }
        /* Remove default browser input styling */
        .login-input input:focus,
        .login-input .ant-input:focus {
          box-shadow: none !important;
        }

        .sign-in-btn {
          width: 100% !important;
          height: 46px !important;
          border-radius: 11px !important;
          background: linear-gradient(135deg, #001529 0%, #1890ff 100%) !important;
          border: none !important;
          font-size: 14.5px !important;
          font-weight: 700 !important;
          letter-spacing: 0.3px !important;
          box-shadow: 0 6px 20px rgba(24,144,255,0.35) !important;
        }
        .sign-in-btn:hover { opacity: 0.92 !important; transform: translateY(-1px) !important; }

        .card-footer {
          display: flex; align-items: center; justify-content: center;
          gap: 7px; margin-top: 22px;
        }
        .footer-dot { width: 3px; height: 3px; border-radius: 50%; background: #cbd5e1; flex-shrink: 0; }
        .footer-txt { color: #94a3b8; font-size: 11px; }

        /* ─── Responsive ─── */
        @media (max-width: 768px) {
          body, html, #root { overflow: auto; }
          .login-root { flex-direction: column; height: auto; min-height: 100vh; }
          .login-left { flex: none; width: 100%; min-height: auto; padding: 28px 20px 40px; order: 1; }
          .login-right { flex: none; width: 100%; min-height: auto; padding: 48px 20px; order: 2; }
          .right-content { width: 88%; max-width: 100%; white-space: normal; }
          .feature-item { white-space: normal; }
          .login-card-panel { flex: none; width: 100%; min-height: auto; padding: 28px 20px 40px; }
          .back-to-home {
            top: 16px;
            right: 16px;
            padding: 8px 14px;
            font-size: 12px;
          }
        }
      `}</style>

      <div className="login-root">

        {/* ═══ LEFT - CARD PANEL ═══ */}
        <div className="login-left">
          <div className="login-card">
            <div className="card-accent" />
            <div className="card-body">

              <div className="card-head">
                <span className="head-avatar">👤</span>
                <Title level={3} className="card-title">Welcome back!</Title>
                <Text className="card-sub">Sign in to manage your wellness center</Text>
              </div>

              <div className="form-divider" />

              <Form
                name="login"
                onFinish={onFinish}
                autoComplete="off"
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item
                  name="email"
                  label={<span className="field-label">Email Address</span>}
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                  style={{ marginBottom: 16 }}
                >
                  <Input
                    className="login-input"
                    size="large"
                    prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<span className="field-label">Password</span>}
                  rules={[{ required: true, message: 'Password is required' }]}
                  style={{ marginBottom: 24 }}
                >
                  <Input.Password
                    className="login-input"
                    size="large"
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="sign-in-btn"
                  >
                    {!loading && '🔐  '}Sign In
                  </Button>
                </Form.Item>
              </Form>

              <div className="card-footer">
                <div className="footer-dot" />
                <span className="footer-txt">
                  © {new Date().getFullYear()} Wellness Admin · All rights reserved
                </span>
                <div className="footer-dot" />
              </div>

            </div>
          </div>
        </div>

        {/* ═══ RIGHT - DARK PANEL WITH BACK BUTTON ═══ */}
        <div className="login-right">
          {/* BACK TO HOME BUTTON - Inside right panel */}
          <Link to="/" className="back-to-home">
            <HomeOutlined />
            <span>Back to Home</span>
          </Link>
          
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="blob blob-3" />

          <div className="right-content">
            <div className="logo-chip">🏋️</div>

            <Title className="brand-title">
              Wellness Admin
            </Title>

            <Text className="brand-tagline">
              Your all-in-one platform to manage wellness
              centers, track attendance, and empower your team.
            </Text>

            <div className="feature-list">
              {features.map((f, i) => (
                <div key={i} className="feature-item">
                  <span className="fi">{f.icon}</span>
                  <span className="ft">{f.text}</span>
                </div>
              ))}
            </div>

            <span className="version-tag">v2.0 · Wellness Platform</span>
          </div>
        </div>

      </div>

      <Modal
        title="Subscription Payment Required"
        open={!!expiredPayment}
        onCancel={() => setExpiredPayment(null)}
        footer={null}
        width={560}
        centered
        bodyStyle={{ 
          padding: '14px'
        }}
      >
        {expiredPayment && (
          <>
            {/* STATE 1: Payment Already Submitted - Pending Approval */}
            {expiredPayment.pendingPayment && (
              <div style={{ padding: '8px 0' }}>
                <Alert
                  type={expiredPayment.pendingPayment.in_grace_period ? 'info' : 'warning'}
                  showIcon
                  style={{ marginBottom: 10 }}
                  message={
                    expiredPayment.pendingPayment.in_grace_period 
                      ? '✅ Payment Under Review' 
                      : '⏳ Awaiting Approval'
                  }
                  description={
                    <div style={{ marginTop: 4, fontSize: 11 }}>
                      {expiredPayment.pendingPayment.in_grace_period ? (
                        <p style={{ margin: 0 }}>
                          You can use the system for 2 days while reviewing.
                        </p>
                      ) : (
                        <p style={{ margin: 0 }}>
                          Access locked. Awaiting admin approval.
                        </p>
                      )}
                    </div>
                  }
                />
                
                <div style={{ 
                  background: '#f8fafc', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 8, 
                  padding: 10 
                }}>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 1 }}>Center</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                      {expiredPayment.center_name}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 1 }}>Amount</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1890ff' }}>
                      ₹{new Intl.NumberFormat('en-IN').format(pricingInfo?.amount || expiredPayment.amount)}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 1 }}>UTR</div>
                    <div style={{ 
                      fontSize: 11, 
                      fontWeight: 500, 
                      color: '#334155',
                      fontFamily: 'monospace',
                      background: '#fff',
                      padding: '4px 8px',
                      borderRadius: 4,
                      border: '1px solid #e2e8f0',
                      wordBreak: 'break-all'
                    }}>
                      {expiredPayment.pendingPayment.upi_transaction_id}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 1 }}>Submitted</div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>
                      {new Date(expiredPayment.pendingPayment.submitted_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* STATE 2: No Payment Submitted - Show Payment Form */}
            {!expiredPayment.pendingPayment && (
              <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  background: '#fff'
                }}>
                  <QRCode 
                    value={buildUpiLink({
                      ...expiredPayment,
                      amount: pricingInfo?.amount || expiredPayment.amount
                    })} 
                    size={150} 
                    bordered={false} 
                    errorLevel="H" 
                  />
                </div>
                <div>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 10 }}
                    message="Pay using QR code"
                    description="Submit UTR below for verification."
                  />
                  
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 1 }}>
                    {expiredPayment.center_name}
                  </div>
                  <div style={{ fontSize: 22, lineHeight: 1, fontWeight: 900, color: '#1890ff', marginBottom: 6 }}>
                    ₹{new Intl.NumberFormat('en-IN').format(pricingInfo?.amount || expiredPayment.amount)}
                    <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginLeft: 4 }}>
                      /mo
                    </span>
                  </div>
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: '6px 8px',
                    color: '#334155',
                    fontSize: 11,
                    fontWeight: 600,
                    marginBottom: 8
                  }}>
                    {expiredPayment.upi_id}
                  </div>
                  <Button
                    type="primary"
                    icon={<MobileOutlined />}
                    href={buildUpiLink({
                      ...expiredPayment,
                      amount: pricingInfo?.amount || expiredPayment.amount
                    })}
                    style={{ width: '100%', height: 32, borderRadius: 6, fontWeight: 600, marginBottom: 8, fontSize: 11 }}
                  >
                    Open UPI
                  </Button>
                  <Form form={paymentForm} layout="vertical" onFinish={handlePaymentSubmit}>
                    <Form.Item
                      name="upi_transaction_id"
                      label={<span style={{ fontSize: 10 }}>UTR / Transaction ID</span>}
                      rules={[
                        { required: true, message: 'Enter UTR' },
                        { min: 4, message: 'Too short' }
                      ]}
                      style={{ marginBottom: 6 }}
                    >
                      <Input placeholder="Enter UTR" size="small" style={{ fontSize: 15 }} />
                    </Form.Item>

                    <Form.Item
                      label={<span style={{ fontSize: 10 }}>Payment Screenshot (Optional)</span>}
                      style={{ marginBottom: 8 }}
                    >
                      {!paymentProofPreview ? (
                        <Upload.Dragger
                          beforeUpload={handleFileChange}
                          showUploadList={false}
                          accept="image/*"
                          style={{
                            padding: '12px',
                            background: '#fafafa',
                            border: '1px dashed #d9d9d9',
                            borderRadius: 6
                          }}
                        >
                          <p style={{ margin: 0, fontSize: 11, color: '#666' }}>
                            <CameraOutlined style={{ fontSize: 18, marginBottom: 4 }} />
                            <br />
                            Click or drag image here
                          </p>
                        </Upload.Dragger>
                      ) : (
                        <div style={{ 
                          position: 'relative',
                          border: '1px solid #e2e8f0',
                          borderRadius: 6,
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={paymentProofPreview} 
                            alt="Payment proof" 
                            style={{ 
                              width: '100%', 
                              maxHeight: 120,
                              objectFit: 'cover',
                              display: 'block'
                            }} 
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveFile}
                            style={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              background: 'rgba(255,255,255,0.9)',
                              fontSize: 10
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </Form.Item>

                    <Button type="primary" htmlType="submit" loading={submittingPayment} block size="small" style={{ fontSize: 11 }}>
                      Submit Payment
                    </Button>
                  </Form>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default Login;
